import { TRecSession, ISession} from "./models/session"
import { DeSer} from  "./deser"
import { nowTimestamp } from "./time"
import { IArch, ChannelPort, ArchPP200A } from  "./arch"
import { Chain, IChannel, IModule, IModuleDef, TRecModuleDef, Module } from "./model"
// Demo session source-of-truth: an editable PP200R snapshot kept as plain JSON so
// updating the demo is a data edit + PR, not a code change. The `with { type: 'json' }`
// import attribute is required by Node/ts-node/webpack to load JSON as a module;
// dropping it breaks the build with ERR_IMPORT_ATTRIBUTE_MISSING.
import demoSource from './data/demo.json' with { type: 'json' }

const L = console.log
const now = nowTimestamp()

export const Snapshots = (arch: IArch) : TRecSession => ({
    Default: {
        id: 'DEFAULT',
        name: "Default",
        created: now,
        meta: {
            created: now,
            modified: now,
            accessed: now,
            immutable: false,
            schema: {
                major: 2,
                minor: 0,
                patch: 0
            }
        },
        devices: {},
        chains: [],
    },
    Demo: report(arch,DeSer.unpackSession(demoSource))
})

// Demo cache invalidation is driven by storage metadata, not by session.hw.model:
// demoSource is authored as PP200R and then re-shaped for the current arch by report().
export function demoSessionNeedsReset ({
  currentModel,
  demoSession,
  firstVisit,
  storedModel,
}: {
  currentModel: string
  demoSession?: ISession
  firstVisit: boolean
  storedModel?: string | null
}) : boolean {
  // Rebuild the canned Demo when any reset trigger holds:
  //  - firstVisit            brand-new demo visitor, no prior demo state
  //  - !storedModel          upgraded from a build that predates the demo.hwModel marker
  //  - storedModel !== currentModel  arch switched via the demo URL (e.g. PP200R -> PP32R)
  //  - !demoSession          the Demo session is missing from storage
  return firstVisit || !storedModel || storedModel !== currentModel || !demoSession
}

// Replace only the generated Demo session so user-created sessions survive
// demo endpoint URL changes such as PP200R -> PP32R.
export function refreshDemoSessionForArch ({
  arch,
  sessions,
}: {
  arch: IArch
  sessions: TRecSession
}) : { sessions: TRecSession, demo: ISession } {
  const demo = Snapshots(arch).Demo
  // Return `demo` alongside the merged map so the caller can immediately apply
  // its chains/devices to live state without reading the session back out.
  return {
    sessions: {
      ...sessions,
      Demo: demo,
    },
    demo,
  }
}

export function report (arch: IArch, sess: ISession) : ISession {
  // Source format: full PP200R demo. Smaller arches are cut down by port label.
  // We translate through PP200 port IDs so each target arch keeps equivalent
  // labels where possible and drops channels that physically do not exist.
  const activeFpIds = new Set(
    arch.ports.fpActive
      .flatMap(r => Array.from({length: r.end - r.start + 1}, (_, i) =>
        ChannelPort.toPortId(-(r.start + i), arch)))
      .filter((id): id is string => id !== undefined)
  )

  const reportChannel = (c?: IChannel) => {
    if (!c) return true

    const sourcePortId = ChannelPort.toPortId(c.port, ArchPP200A)
    if (sourcePortId === undefined) return false
    if (sourcePortId.startsWith('FP') && !activeFpIds.has(sourcePortId)) return false

    const port = ChannelPort.fromPortId(sourcePortId, arch)
    if (port === undefined) return false
    c.port = port
    c.portId = ChannelPort.toPortId(port, arch)
    return c.portId !== undefined
  }

  const reportNode = (node: IModuleDef|IModule) =>
    reportChannel(node.L?.from) && reportChannel(node.L?.to)
    && reportChannel(node.R?.from) && reportChannel(node.R?.to)

  /**
   * ARCH-specific cleanup (so demo data can be shared across architectures)
   */
  const $nodes: TRecModuleDef = {}
  Object.values(sess.devices)
  .forEach(device => {
    Object.entries(device.nodes)
      .forEach(([id,node]) => {
        if (!reportNode(node)) {
          // REMOVE out-of-range node
          delete device.nodes[id]
        }
        // INDEX node
        else {
          $nodes[id] = node
        }
      })
  })
  // REMOVE empty devices (ostensibly because all nodes had out-of-range ports)
  Object.entries(sess.devices)
  .forEach(([id,device]) => {
    // NODE-less device
    if (Object.values(device.nodes).length === 0) {
      delete sess.devices[id]
    }
  })
  // REMOVE chains referencing deleted nodes (ostensibly removed above)
  sess.chains = sess.chains.filter(chain => Chain.nodes(chain).every(node => $nodes[node.id]))
  // UPDATE chain ports to match re-ported devices above
  sess.chains.forEach(chain => Chain.nodes(chain).forEach(node => reportNode(node)))
  // REMOVE chains having conflicting nodes due to indirectly conflicting ports (e.g. PP64: C1 <-> P1)
  sess.chains = sess.chains
    .filter(chain => Chain.nodes(chain)
        .every((node,i,nodes) => nodes
            .every(($node,j) => i === j // SELF-COMPARE is always non-conflicting
                || !Module.conflicting(node,$node,arch)))
  )
  return sess
}
